import { observer } from 'mobx-react-lite';
import { useComputed } from 'owa-react-hooks/lib/useComputed';

import { Folder, isPublicFolder } from 'owa-folders';
import { isFavoritingInProgress } from 'owa-mail-favorites-store';
import { lazyToggleFavoriteFolder } from 'owa-mail-folder-forest-actions';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import { lazyRemovePublicFolderFromFavorites } from 'owa-publicfolder-picker';
import * as React from 'react';
import {
    ToggleFavoriteButton,
    isFolderInFavorites,
    isPublicFolderInFavorites,
} from 'owa-favorites';
import { isFolderOfTreeType } from 'owa-folders';
import { SHARED_FOLDERS_TREE_TYPE, ARCHIVE_FOLDERS_TREE_TYPE } from 'owa-folders-constants';

const styles = require('./MailListHeaderSecondRow.scss');
const classNames = require('classnames');

export interface MailListHeaderSecondRowTextContentProps {
    text: string;
    containerCssClass?: string;
}

export default observer(function MailListHeaderSecondRowTextContent(
    props: MailListHeaderSecondRowTextContentProps
) {
    const folder_0 = useComputed(
        (): Folder => {
            return getSelectedFolder();
        }
    );
    const isPublicFolder_0 = useComputed((): boolean => {
        return (
            folder_0.get() && folder_0.get().FolderId && isPublicFolder(folder_0.get().FolderId.Id)
        );
    });
    const isSharedFolder = useComputed((): boolean => {
        return (
            folder_0.get() &&
            folder_0.get().FolderId &&
            isFolderOfTreeType(folder_0.get().FolderId.Id, SHARED_FOLDERS_TREE_TYPE)
        );
    });
    const isArchiveFolder = useComputed((): boolean => {
        return (
            folder_0.get() &&
            folder_0.get().FolderId &&
            isFolderOfTreeType(folder_0.get().FolderId.Id, ARCHIVE_FOLDERS_TREE_TYPE)
        );
    });
    const isFolderInFavorite = useComputed((): boolean => {
        if (!folder_0.get() || !folder_0.get().FolderId) {
            return false;
        }
        return isPublicFolder_0.get()
            ? isPublicFolderInFavorites(folder_0.get().FolderId.Id)
            : isFolderInFavorites(folder_0.get().FolderId.Id);
    });
    const toggleFavoriteState = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        const folder = folder_0.get();
        if (!folder || !folder.FolderId) {
            return;
        }
        if (isFavoritingInProgress(folder.FolderId.Id)) {
            // Do nothing: favoriting is in progress
            return;
        }
        if (isPublicFolder_0.get()) {
            toggleFavoritePublicFolder(folder);
        } else {
            lazyToggleFavoriteFolder.importAndExecute(folder, 'FolderHeader');
        }
    };
    const toggleFavoritePublicFolder = async folder => {
        const removePublicFolderFromFavorites = await lazyRemovePublicFolderFromFavorites.import();
        removePublicFolderFromFavorites(folder);
    };
    const shouldShowToggleFavorite = !isSharedFolder.get() && !isArchiveFolder.get();
    return (
        <div
            className={classNames(styles.customTextContainer, props.containerCssClass)}
            role="heading"
            aria-level={2}>
            <span
                className={classNames(
                    styles.customHeaderText,
                    shouldShowToggleFavorite && styles.customHeaderTextWithFavorite
                )}>
                {props.text}
            </span>
            {folder_0.get() && shouldShowToggleFavorite && (
                <ToggleFavoriteButton
                    isInFavorites={isFolderInFavorite.get()}
                    onClick={toggleFavoriteState}
                    tooltipStyles={styles.toggleFavoriteTooltip}
                />
            )}
        </div>
    );
});
